import dayjs from 'dayjs';
import { pb } from '../../../shared/api/pocketbase';
import { logAction } from '../../../shared/lib/userLogger';
import { Assignment } from './types';

function planDateToDay(planDate: string): string {
  return planDate.slice(0, 10);
}

export function todayStr(): string {
  return dayjs().format('YYYY-MM-DD');
}

export function tomorrowStr(): string {
  return dayjs().add(1, 'day').format('YYYY-MM-DD');
}

const ASSIGNMENT_FIELDS = 'id,name,planDate,status,result,created,updated,authorId,ownerId,expand.authorId.id,expand.authorId.name,expand.authorId.email,expand.ownerId.id,expand.ownerId.name,expand.ownerId.email';
const ASSIGNMENT_WITH_TASK_FIELDS = `${ASSIGNMENT_FIELDS},tasksId,expand.tasksId.id,expand.tasksId.title`;

export async function fetchAssignments(): Promise<Assignment[]> {
  const tomorrow = tomorrowStr();
  return pb.collection('jobsTasks').getFullList<Assignment>({
    filter: `planDate <= "${tomorrow} 23:59:59" && status != "COMPLETED"`,
    sort: 'planDate',
    expand: 'authorId,ownerId,tasksId',
    fields: ASSIGNMENT_WITH_TASK_FIELDS,
  });
}

export async function createAssignment(data: {
  name: string;
  tasksId?: string;
  ownerId?: string;
  planDate: string;
}): Promise<Assignment> {
  const created = await pb.collection('jobsTasks').create<Assignment>({
    ...data,
    authorId: pb.authStore.model?.id ?? '',
    actual: true,
  }, {
    expand: 'authorId,ownerId',
    fields: ASSIGNMENT_FIELDS,
  });

  logAction(`Created assignment: ${data.name}`, 'ADD');
  return created;
}

export async function fetchAssignmentByTaskId(taskId: string): Promise<Assignment | null> {
  try {
    return await pb.collection('jobsTasks').getFirstListItem<Assignment>(
      `tasksId = "${taskId}" && result = ""`,
      { fields: 'id' },
    );
  } catch {
    return null;
  }
}

export async function fetchCompletedAssignmentsByTaskId(taskId: string): Promise<Assignment[]> {
  return pb.collection('jobsTasks').getFullList<Assignment>({
    filter: `tasksId = "${taskId}" && result != ""`,
    sort: '-created',
    expand: 'authorId,ownerId',
    fields: ASSIGNMENT_FIELDS,
  });
}

export async function fetchAllAssignmentsByTaskId(taskId: string): Promise<Assignment[]> {
  return pb.collection('jobsTasks').getFullList<Assignment>({
    filter: `tasksId = "${taskId}"`,
    sort: 'planDate',
    expand: 'authorId,ownerId',
    fields: ASSIGNMENT_FIELDS,
  });
}

export async function resolveAssignment(id: string, result: string): Promise<Assignment> {
  const resolved = await pb.collection('jobsTasks').update<Assignment>(id, {
    result,
    status: 'COMPLETED',
  }, {
    expand: 'authorId,ownerId',
    fields: ASSIGNMENT_FIELDS,
  });

  logAction(`Resolved assignment: ${resolved.name || id}`, 'EDIT');
  return resolved;
}

function getColumnFilter(column: 'overdue' | 'today' | 'tomorrow'): string {
  const today = todayStr();
  const tomorrow = tomorrowStr();
  if (column === 'overdue') {
    return `status = "EXPIRED"`;
  }
  if (column === 'today') {
    return `status != "COMPLETED" && status != "EXPIRED" && planDate >= "${today} 00:00:00" && planDate < "${tomorrow} 00:00:00"`;
  }
  return `status != "COMPLETED" && status != "EXPIRED" && planDate >= "${tomorrow} 00:00:00" && planDate <= "${tomorrow} 23:59:59"`;
}

export async function fetchAssignmentsPage(
  column: 'overdue' | 'today' | 'tomorrow',
  page: number,
  perPage: number,
  userIds?: string[],
  filterMode?: 'any' | 'author' | 'owner',
): Promise<{ items: Assignment[]; totalItems: number }> {
  const filterParts = [getColumnFilter(column)];
  if (userIds && userIds.length > 0) {
    let userFilter: string;
    if (filterMode === 'author') {
      userFilter = userIds.map(id => `authorId = "${id}"`).join(' || ');
    } else if (filterMode === 'owner') {
      userFilter = userIds.map(id => `ownerId = "${id}"`).join(' || ');
    } else {
      userFilter = userIds.map(id => `(ownerId = "${id}" || authorId = "${id}")`).join(' || ');
    }
    filterParts.push(`(${userFilter})`);
  }
  const result = await pb.collection('jobsTasks').getList<Assignment>(page, perPage, {
    filter: filterParts.join(' && '),
    sort: 'planDate',
    expand: 'authorId,ownerId,tasksId',
    fields: ASSIGNMENT_WITH_TASK_FIELDS,
    requestKey: `tasks-col-${column}-p${page}-${filterMode ?? 'any'}-${userIds?.join(',') ?? ''}`,
  });
  return { items: result.items, totalItems: result.totalItems };
}

const CONTROL_FIELDS = 'id,name,planDate,status,tasksId,ownerId,expand.tasksId.id,expand.tasksId.title,expand.ownerId.id,expand.ownerId.name,expand.ownerId.email';

// ~40 id × ~25 симв ≈ 1000 симв на фильтр — с запасом под лимит PocketBase
const OWNER_CHUNK = 40;
const chunk = <T>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Срочные (< 1 часа до срока) и просроченные задачи указанных исполнителей
export async function fetchControlAssignments(userIds: string[]): Promise<{
  urgent: Assignment[];
  overdue: Assignment[];
}> {
  if (!userIds.length) return { urgent: [], overdue: [] };

  const now = dayjs();
  // planDate хранится как UTC-инстант в формате "YYYY-MM-DD HH:mm:ss.000Z".
  // Границы тоже в UTC и в том же формате (пробел, не "T") — иначе смещение таймзоны
  // (в MSK +3ч) тянет будущие задачи в просрочку, а ISO с "T" ломает сравнение дат в PB.
  const toUtc = (d: dayjs.Dayjs) => d.toISOString().slice(0, 19).replace('T', ' ');
  const nowStr = toUtc(now);
  const hourStr = toUtc(now.add(1, 'hour'));

  // ponytail: бьём ownerId на чанки — иначе длинный OR-фильтр упирается в лимит длины фильтра PocketBase
  const dedupeById = (rows: Assignment[]) => [...new Map(rows.map(r => [r.id, r])).values()];

  const chunks = chunk(userIds, OWNER_CHUNK);
  const urgentBatches: Assignment[][] = [];
  const overdueBatches: Assignment[][] = [];
  await Promise.all(chunks.flatMap((ids, i) => {
    const owner = `(${ids.map(id => `ownerId = "${id}"`).join(' || ')}) && status != "COMPLETED"`;
    return [
      pb.collection('jobsTasks').getFullList<Assignment>({
        filter: `${owner} && planDate >= "${nowStr}" && planDate <= "${hourStr}"`,
        sort: 'planDate',
        expand: 'tasksId,ownerId',
        fields: CONTROL_FIELDS,
        requestKey: `ctrl-urgent-${i}`,
      }).then(r => { urgentBatches[i] = r; }),
      pb.collection('jobsTasks').getFullList<Assignment>({
        filter: `${owner} && planDate < "${nowStr}"`,
        sort: '-planDate',
        expand: 'tasksId,ownerId',
        fields: CONTROL_FIELDS,
        requestKey: `ctrl-overdue-${i}`,
      }).then(r => { overdueBatches[i] = r; }),
    ];
  }));

  const urgent = dedupeById(urgentBatches.flat()).sort((a, b) => a.planDate.localeCompare(b.planDate));
  const overdue = dedupeById(overdueBatches.flat()).sort((a, b) => b.planDate.localeCompare(a.planDate));

  return { urgent, overdue };
}

export function assignmentBelongsToColumn(
  planDate: string,
  column: 'overdue' | 'today' | 'tomorrow',
  status?: Assignment['status'],
): boolean {
  if (column === 'overdue') return status === 'EXPIRED';
  if (status === 'EXPIRED' || status === 'COMPLETED') return false;
  const day = planDate.slice(0, 10);
  const today = todayStr();
  const tomorrow = tomorrowStr();
  if (column === 'today') return day === today;
  return day === tomorrow;
}

export function groupAssignments(items: Assignment[]): {
  overdue: Assignment[];
  today: Assignment[];
  tomorrow: Assignment[];
} {
  const todayDate = todayStr();
  const tomorrowDate = tomorrowStr();

  return {
    overdue: items.filter(a => planDateToDay(a.planDate) < todayDate),
    today: items.filter(a => planDateToDay(a.planDate) === todayDate),
    tomorrow: items.filter(a => planDateToDay(a.planDate) === tomorrowDate),
  };
}
